import { DateTime } from 'luxon';
import { ActionContext } from 'vuex';
import { AddToCartRes, CartRecipe, Coupon, FullOrder, HistoryOrder, ItemPriceCal, LineItem, OrderLogistics, OrderTotals } from '@/models/cartModels';
import {
    addToCart,
    deleteFromCart,
    updateCartItem,
    orderPrice,
    submitOrder,
    buildOrderConfirmation,
    replaceCart,
    addOrderToCart,
    fetchToppings,
    mapMenuItemToLineItem,
    itemPrice,
    simpleLineItemMapper,
    mapHalfAndHalfToLineItem,
    mergeToppingOptions,
    isHalfSigToppingsValid,
    isPizzaCoverageValid,
    isToppingLimitReached,
    getOverLimitMessage,
    dressingVariety,
} from '@/services/orderService';
import { deleteLocalStorage, getLocalStorage, localStorageEnum, replaceLocalStorage, setLocalStorage } from '@/services/localStorageService';
import { Payment } from '@/models/PaymentModels';
import { PaymentIdEnum } from '@/enums/PaymentTypeEnum';
import { errorToast, successToastDetail } from '@/services/pageService';
import selectedLocationModule from '@/store/selectedLocationModule';
import { createSlug, randomlyFillArr, stringToBool } from '@/services/stringService';
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics';
import { cartTotalsFromGetters, getBrazeCartId, resetBrazeCartId, trackCartUpdated, trackProductViewed } from '@/services/braze/ecommerce';
import { AccountState } from '@/store/accountModule';
import { ValidateBasketPayload, ValidatedBasketItem } from '@/models/account/rewardModels';
import { validateRewardBasketItems } from '@/services/accountService';
import { BasketValidationDTO, DiscountBasketItemValidationDTO } from '@/models/account/rewardDTOModels';
import {
    AvailableTopping,
    CurrentItemDetails,
    InitLineItemDispatch,
    Instruction,
    InstructionOption,
    MenuCategory,
    MenuItem,
    MenuItemSelection,
    MenuItemSelectionLookup,
    PizzaToppingOption,
    Recipe,
    RequiredTopping,
    Size,
    Topping,
    ToppingReq,
    ToppingQuantityOption,
    TwoSidedSelection,
} from '@/models/menuModels';
import { CustomMenuItemNameEnum } from '@/enums/CustomMenuItemNameEnum';
import { PizzaSideEnum } from '@/enums/PizzaSideEnum';
import { HOUSEMADE_RANCH_SIZE_ID, SAUCE_CUP_ITEM_NAME } from '@/constants';

export interface OrderState {
    deliveryCharge?: number;
    cartItems: ReadonlyArray<LineItem>;
    estimatedTime?: DateTime;
    validCoupons: ReadonlyArray<Coupon>;
    payments: ReadonlyArray<any>;
    tip: number;
    taxTotal: number;
    grandTotal?: number;
    submittedOrder?: FullOrder;
    orderProcessing: boolean;
    orderError?: string;
    latestMessage: string;
    isEditCartLoader: boolean;
    skipPriceAlignment: boolean;
    isCartUpdating: boolean;
    isToppingModalOpen: boolean;
    appliedRewardDiscount: number;
    rewardItemNameFailures: ReadonlyArray<string>;
    validatedBasketItems: ReadonlyArray<ValidatedBasketItem>;
    isRewardsValidating: boolean;
    rewardsNeedValidation: boolean;
    recommendItems: ReadonlyArray<MenuItem> | null;
    currentItemDetails: CurrentItemDetails;
}

const orderState: OrderState = {
    tip: 0,
    taxTotal: 0,
    cartItems: [],
    validCoupons: getLocalStorage(localStorageEnum.coupons) ?? [],
    payments: [],
    orderProcessing: false,
    latestMessage: '',
    appliedRewardDiscount: 0,
    isEditCartLoader: false,
    skipPriceAlignment: false,
    isCartUpdating: false,
    isToppingModalOpen: false,
    rewardItemNameFailures: [],
    validatedBasketItems: [],
    isRewardsValidating: false,
    rewardsNeedValidation: false,
    recommendItems: null,
    currentItemDetails: {
        menuItem: null,
        menuCategory: null,
        lineItem: null,
        selectedCrust: null,
        selectedSize: null,
        availableTopping: null,
        defaultCrustSauce: null,
        priceAndCal: null,
        isNewLineItem: true,
        isSauceCustomized: false,
        isToppingsValid: false,
        requiredSelectionMessage: null,
        lastSauceCup: null,
    },
};

export default {
    state: orderState,
    mutations: {
        SET_MENU_CATEGORY: (state: OrderState, menuCategory: MenuCategory) => {
            state.currentItemDetails.menuCategory = menuCategory;
        },
        SET_MENU_ITEM: (state: OrderState, menuItem: MenuItem) => {
            state.currentItemDetails.menuItem = menuItem;
        },
        SET_LINE_ITEM: (state: OrderState, lineItem: LineItem) => {
            state.currentItemDetails.lineItem = lineItem;
        },
        SET_IS_NEW_LINE_ITEM: (state: OrderState, val: boolean) => {
            state.currentItemDetails.isNewLineItem = val;
        },
        SET_IS_SAUCE_CUSTOMIZED: (state: OrderState, val: boolean) => {
            state.currentItemDetails.isSauceCustomized = val;
        },
        SET_LINE_ITEM_PRICE: (state: OrderState, price: number) => {
            if (state.currentItemDetails.lineItem) {
                state.currentItemDetails.lineItem.price = price;
            }
        },
        SET_LINE_ITEM_QUANTITY: (state: OrderState, quantity: number) => {
            if (state.currentItemDetails.lineItem) {
                state.currentItemDetails.lineItem.quantity = quantity;
            }
        },
        SET_PRICE_AND_CAL: (state: OrderState, priceAndCal: ItemPriceCal | null) => {
            state.currentItemDetails.priceAndCal = priceAndCal;
        },
        RESET_CURRENT_ITEM_DETAILS: (state: OrderState) => {
            state.currentItemDetails = {
                menuItem: null,
                menuCategory: null,
                lineItem: null,
                selectedCrust: null,
                selectedSize: null,
                availableTopping: null,
                defaultCrustSauce: null,
                priceAndCal: null,
                isNewLineItem: true,
                isSauceCustomized: false,
                isToppingsValid: false,
                requiredSelectionMessage: null,
                lastSauceCup: null,
            };
        },
        SET_LAST_SAUCE_CUP: (state: OrderState, sauceCup: RequiredTopping | null) => {
            state.currentItemDetails.lastSauceCup = sauceCup;
        },
        SET_SELECTED_FLAVOR: (state: OrderState, flavor: Topping | null) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedFlavors = flavor ? [flavor] : [];
        },
        SET_RECOMMEND_ITEMS: (state: OrderState, recommendItems: Array<MenuItem>) => {
            state.recommendItems = recommendItems;
        },
        SET_SAUCE_TOPPING: (state: OrderState, sauces: Array<Topping> | undefined) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedSauces = sauces ?? [];
        },
        SET_MEAT_AND_VEGGIE_TOPPING: (state: OrderState, toppings: Array<Topping>) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedToppings = toppings;
        },
        SET_DRESSING_TOPPING: (state: OrderState, dressings: Array<Topping>) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedDressings = dressings.filter((d) => d.selected);
        },
        SET_SAUCE_CUP: (state: OrderState, topping: Topping | null) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedRequiredToppings = topping ? [topping] : [];
        },
        SET_SAUCE_CUP_TWO_SIDED: (state: OrderState, topping: Topping | null) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedRequiredToppings = topping ? [topping] : [];
            state.currentItemDetails.lineItem.selectedRequiredToppings2 = topping ? [topping] : [];
        },
        SET_INSTRUCTIONS: (state: OrderState, instructions: Array<Instruction>) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.selectedInstructions = instructions;
        },
        SET_SELECTED_CRUST: (state: OrderState, crust: Recipe) => {
            state.currentItemDetails.selectedCrust = crust;
        },
        SET_SELECTED_SIZE: (state: OrderState, size: Size) => {
            state.currentItemDetails.selectedSize = size;
        },
        SET_RECIPES: (state: OrderState, recipe: Array<CartRecipe>) => {
            if (!state.currentItemDetails.lineItem) return;
            state.currentItemDetails.lineItem.recipes = recipe;
            // Two-sided pizzas share one crust, so keep the second half's recipe/size in sync for correct pricing
            if (state.currentItemDetails.lineItem.recipes2) {
                state.currentItemDetails.lineItem.recipes2 = recipe;
            }
        },
        SET_AVAILABLE_TOPPING: (state: OrderState, availableTopping: AvailableTopping) => {
            state.currentItemDetails.availableTopping = availableTopping;
        },
        SET_CRUST_DEFAULT_SAUCE: (state: OrderState, defaultSauce: Topping) => {
            state.currentItemDetails.defaultCrustSauce = defaultSauce;
        },
        SET_IS_TOPPINGS_VALID: (state: OrderState, isValid: boolean) => {
            state.currentItemDetails.isToppingsValid = isValid;
        },
        SET_REQUIRED_SELECTION_MESSAGE: (state: OrderState, message: string | null) => {
            state.currentItemDetails.requiredSelectionMessage = message;
        },
        SET_IS_TOPPING_MODAL_OPEN: (state: OrderState, val: boolean) => {
            state.isToppingModalOpen = val;
        },
        SET_CART_IS_UPDATING: (state: OrderState, val: boolean) => {
            state.isCartUpdating = val;
        },
        SET_SKIP_PRICE_ALIGNMENT: (state: OrderState, val: boolean) => {
            state.skipPriceAlignment = val;
        },
        ADD_CART_ITEM: (state: OrderState, item: LineItem): void => {
            state.cartItems = [...state.cartItems, item];
            replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },
        REMOVE_CART_ITEM: (state: OrderState, lineItem: LineItem): void => {
            const removeIndex = state.cartItems.findIndex((cart) => cart == lineItem);
            if (removeIndex > -1) {
                state.cartItems = [...state.cartItems.slice(0, removeIndex), ...state.cartItems.slice(removeIndex + 1)];
                replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
            }
        },
        // Turns bundle items back into ordinary line items. Mutated in place because REMOVE_CART_ITEM
        // matches by reference, so these objects have to keep their identity.
        DETACH_BUNDLE_ITEMS: (state: OrderState, identifier: string): void => {
            state.cartItems.forEach((item) => {
                if (item.bundleIdentifier?.startsWith(identifier)) {
                    item.bundleIdentifier = undefined;
                }
            });
            state.cartItems = [...state.cartItems];
            replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },
        UPDATE_CART_ITEM: (state: OrderState, item: LineItem): void => {
            const removeIndex = state.cartItems.findIndex((cart) => +(cart.lineitemId ?? 0) == +(item.lineitemId ?? 0));
            if (removeIndex > -1) {
                state.cartItems = [...state.cartItems.slice(0, removeIndex), item, ...state.cartItems.slice(removeIndex + 1)];
                replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
            }
        },
        CLEAR_CART: (state: OrderState): void => {
            state.cartItems = [];
            deleteLocalStorage(localStorageEnum.cart);
            deleteLocalStorage(localStorageEnum.appliedRewards);
        },
        LOGOUT_CART: (state: OrderState): void => {
            state.cartItems = [];
            // logout will now start a fresh session instead of holding onto any cart data
            deleteLocalStorage(localStorageEnum.cart);
            deleteLocalStorage(localStorageEnum.orderLogistics);
            deleteLocalStorage(localStorageEnum.coupons);
            deleteLocalStorage(localStorageEnum.appliedRewards);
        },

        UPDATE_ESTIMATED_TIME: (state: OrderState, date: string) => {
            state.estimatedTime = DateTime.fromSQL(date);
        },
        ADD_COUPON: (state: OrderState, coupon: Coupon) => {
            state.validCoupons = [...state.validCoupons, coupon];
        },
        REMOVE_COUPON: (state: OrderState, coupon: Coupon) => {
            const { trackRemoveCoupon } = useGoogleAnalytics();
            const removeIndex = state.validCoupons.findIndex((valid) => valid.name === coupon.name);
            if (removeIndex > -1) {
                trackRemoveCoupon(coupon.code);
                state.validCoupons = [...state.validCoupons.slice(0, removeIndex), ...state.validCoupons.slice(removeIndex + 1)];
            }
        },
        REPLACE_ALL_COUPONS: (state: OrderState, coupons: Coupon[]) => {
            const newCoupons: Coupon[] = [];
            coupons.forEach((apiCoupon) => {
                const oldCoupon = state.validCoupons.find((localCoupon) => localCoupon.phoenixCode === apiCoupon.phoenixCode);
                if (oldCoupon) {
                    // remove coupon from state so it can't get processed twice
                    // single use coupons use the same phoenixCode, this ensures we move on
                    // to the next available singleUseCoupon instead of duplicating the first one found
                    const index = state.validCoupons.indexOf(oldCoupon);
                    if (index > -1) state.validCoupons = [...state.validCoupons.slice(0, index), ...state.validCoupons.slice(index + 1)];
                    // Keep .campaign, .campaignCodeId, and .code from user entered coupon
                    // .code gets replaced with phoenixCode by the api but singleUseCodes also uses .code field
                    apiCoupon.campaign = oldCoupon.campaign;
                    apiCoupon.campaignCodeId = oldCoupon.campaignCodeId;
                    apiCoupon.code = oldCoupon.code;
                    newCoupons.push(apiCoupon);
                } else {
                    newCoupons.push(apiCoupon);
                }
            });
            state.validCoupons = newCoupons;
        },
        RESET_COUPONS: (state: OrderState) => {
            state.validCoupons = [];
        },
        UPDATE_TIP: (state: OrderState, tip: number) => (state.tip = tip),
        UPDATE_PRICES: (state: OrderState, fullOrder: FullOrder): void => {
            state.taxTotal = +(fullOrder?.orderDetails?.taxTotal ?? 0);
            state.grandTotal = +(fullOrder?.orderDetails?.grandTotal ?? 0);

            // Delivery Charge lives with the selected location not on the order.
            const isDelivery = fullOrder.orderDetails.orderLogistics.orderType?.toLocaleUpperCase() == 'DELIVERY';
            const deliveryCharge: number = isDelivery ? +(selectedLocationModule.state.location?.deliveryCharge ?? 0) : 0.0;
            state.deliveryCharge = deliveryCharge;
        },
        UPDATE_ITEM_PRICES: (state: OrderState, fullOrder: FullOrder): void => {
            const orderItems: any[] = [Object.values(fullOrder.orderDetails.lineItems)].flat();
            const cartItems = state.cartItems;
            // originally based on item.id but miltiple items can have the same id with different customizations
            // this was causing all similar items to have the same price even if they had extra/less toppings
            if (orderItems && cartItems) {
                cartItems.forEach((item, index1) => {
                    orderItems.forEach((orderItem, index2) => {
                        if (index1 == index2) {
                            item.price = orderItem.price;
                        }
                    });
                });
            }
            replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },
        ADD_ORDER_PAYMENT: (state: OrderState, payment: Payment) => {
            const newPayments = [...state.payments, payment];
            const giftCardPayments = newPayments.filter((p) => p.paymentType === PaymentIdEnum.giftCard);
            //sort to apply lowest balance gift cards first to ensure maximum utilization of gift cards and reduce leftover balances
            const sortedGiftCards = giftCardPayments.sort((a, b) => +a.balance - +b.balance);
            const otherPayments = newPayments.filter((p) => p.paymentType !== PaymentIdEnum.giftCard);
            state.payments = [...sortedGiftCards, ...otherPayments];
        },
        REMOVE_ORDER_PAYMENT: (state: OrderState, payment: any) => {
            let removeIndex;
            if (payment.paymentType != PaymentIdEnum.giftCard) {
                removeIndex = state.payments.findIndex((p) => p.paymentType == payment.paymentType);
            } else {
                removeIndex = state.payments.findIndex((p) => p.cardNumber === payment.cardNumber);
            }
            if (removeIndex > -1) {
                state.payments = [...state.payments.slice(0, removeIndex), ...state.payments.slice(removeIndex + 1)];
            }
        },
        SET_PAYMENT_AMOUNTS: (state: OrderState, fullOrder: FullOrder) => {
            let localGrandTotal = fullOrder.orderDetails.grandTotal ?? 0;
            fullOrder.orderDetails.payments = fullOrder.orderDetails.payments.map((payment) => {
                if (payment.paymentType === PaymentIdEnum.giftCard) {
                    const amountToApply = Math.min(+payment.balance, localGrandTotal);
                    localGrandTotal -= amountToApply;
                    payment.amount = amountToApply;
                } else if (payment.paymentType === PaymentIdEnum.credit) {
                    payment.amount = localGrandTotal;
                }
                return payment;
            });
        },
        UPDATE_ORDER_PAYMENTS: (state: OrderState, grandTotal: number) => {
            if (state.payments.length === 0) return;

            const giftCardPayments = state.payments.filter((p) => p.paymentType === PaymentIdEnum.giftCard);
            const giftcardsTotal = giftCardPayments.reduce((total, payment) => total + +payment.balance, 0);

            // if gift cards cover total, set gift card amounts and remove other payment types
            let indexStart;
            if (giftcardsTotal >= grandTotal) {
                let runningTotal = 0;
                for (let index = 0; index < giftCardPayments.length; index++) {
                    const payment = giftCardPayments[index];
                    runningTotal += +payment.balance;
                    if (runningTotal >= grandTotal && state.payments.length > index + 1) {
                        indexStart = index + 1;
                        break;
                    }
                }
                if (indexStart) {
                    state.payments = [...state.payments.slice(0, indexStart)];
                }
            }

            //if grandTotal is 0 or less. remove all payments
            if (grandTotal <= 0) {
                state.payments = [];
            }
        },
        CLEAR_ORDER_PAYMENTS: (state: OrderState) => (state.payments = []),
        SET_ORDER_PROCESSING: (state: OrderState, processing: boolean) => (state.orderProcessing = processing),
        SET_ORDER_ERROR: (state: OrderState, error: string) => (state.orderError = error),
        SUBMITTED_ORDER: (state: OrderState, order: FullOrder) => (state.submittedOrder = new FullOrder(order)),
        UPDATE_LATEST_MESSAGE: (state: OrderState, message: string) => (state.latestMessage = message),
        SET_REWARD_DISCOUNT: (state: OrderState, val: number) => (state.appliedRewardDiscount = val),
        SET_CARTEDIT_LOADER: (state: OrderState, val: boolean) => (state.isEditCartLoader = val),
        SET_BASKET_ITEM_FAILURES: (state: OrderState, failures: ReadonlyArray<DiscountBasketItemValidationDTO>): void => {
            state.rewardItemNameFailures = failures.map((f) => f.discountDetails.name);
        },
        SET_VALIDATED_BASKET_ITEMS: (state: OrderState, items: ReadonlyArray<DiscountBasketItemValidationDTO>): void => {
            state.validatedBasketItems = items.map((item) => {
                return {
                    rewardId: item.discountId,
                    name: item.discountDetails.name,
                    type: item.discountType,
                    description: item.discountDetails.description,
                    discountAmount: item.discountAmount,
                };
            });
            replaceLocalStorage(localStorageEnum.appliedRewards, state.validatedBasketItems, 1);
        },
        SET_REWARDS_VALIDATING: (state: OrderState, val: boolean) => (state.isRewardsValidating = val),
        SET_REWARDS_NEED_VALIDATION: (state: OrderState, val: boolean) => (state.rewardsNeedValidation = val),
    },
    actions: {
        setMenuItemFromLineItem(context: ActionContext<OrderState, any>, lineItem: LineItem) {
            const locationMenu = context.getters.getLocationMenu;

            let menuItem: MenuItem | null = null;

            locationMenu.some((category) => {
                if (lineItem.name2) {
                    menuItem = category.items.find((c) => c.itemDisplayName === CustomMenuItemNameEnum.HalfAndHalf);
                } else {
                    menuItem = category.items.find((i) => i.itemExternalId == lineItem.id || (i.itemDisplayName == lineItem.name && lineItem.category == i.categoryName));
                }
                return !!menuItem;
            });
            context.commit('SET_MENU_ITEM', menuItem);
        },
        async initLineItem(context: ActionContext<OrderState, any>, payload: InitLineItemDispatch): Promise<void> {
            context.commit('SET_IS_SAUCE_CUSTOMIZED', false);
            context.commit('SET_IS_NEW_LINE_ITEM', true);

            try {
                const menuCategory = context.getters.getLocationMenu?.find((c) => createSlug(c.name) === payload.category);
                context.commit('SET_MENU_CATEGORY', menuCategory ?? null);

                const menuItem = menuCategory?.items?.find((item) => item.path === payload.itemName);
                const recipe = menuItem?.recipes?.find((r) => r.preselected) ?? menuItem?.firstRecipe; //crust
                const size = recipe?.sizes?.find((s) => s.preselected) ?? recipe?.sizeDescending[0];

                context.commit('SET_SELECTED_CRUST', recipe);
                context.commit('SET_SELECTED_SIZE', size);
                context.commit('SET_MENU_ITEM', menuItem);

                if (!size || !recipe || !menuItem) {
                    return;
                }

                const availableTopping = await context.dispatch('setAvailableTopping', { menuItem, recipe, size });

                const lineItem = mapMenuItemToLineItem(menuItem, size, recipe, availableTopping);
                // Half & Half starts from the builder item, whose id would otherwise make the
                // left side look pre-selected. Clear it so a side counts as selected only once
                // the user actually picks one.
                if (menuItem.itemDisplayName === CustomMenuItemNameEnum.HalfAndHalf) {
                    lineItem.id = '';
                }
                context.commit('SET_LINE_ITEM', lineItem);

                context.dispatch('setLineItemDefaults');

                context.dispatch('validateRequiredSelections');

                // Half & Half has no meaningful price until both sides are selected.
                if (menuItem.itemDisplayName !== CustomMenuItemNameEnum.HalfAndHalf || context.state.currentItemDetails.isToppingsValid) {
                    await context.dispatch('updateLineItemPriceAndCal');
                }

                const viewedLineItem = context.state.currentItemDetails.lineItem;
                if (viewedLineItem) trackProductViewed(menuItem, viewedLineItem);
            } catch (error) {
                throw new Error('Failed to initialize line item.');
            }

            return;
        },
        setLineItemDefaults(context: ActionContext<OrderState, any>): void {
            const availableTopping = context.state.currentItemDetails.availableTopping;
            const menuItem = context.state.currentItemDetails.menuItem;

            if (!availableTopping || !menuItem) return;

            context.dispatch('setCrustDefaultSauce');

            const crust = context.state.currentItemDetails.selectedCrust;
            if (crust?.isHandTossed) {
                const sauceCup = context.getters.getSauceCups?.[0];
                if (sauceCup) context.dispatch('updateSauceCup', sauceCup);
            }

            if ((menuItem.isAppetizer && availableTopping.flavors.length > 0) || availableTopping.flavors.length === 1) {
                const firstFlavor = new Topping(availableTopping.flavors[0]);
                firstFlavor.selected = true;
                context.commit('SET_SELECTED_FLAVOR', firstFlavor);
            }
        },
        async setEditLineItem(context: ActionContext<OrderState, any>, lineItemId: string): Promise<void> {
            context.commit('SET_IS_NEW_LINE_ITEM', false);

            const editCartItem = context.getters.getCartItems.find((c) => String(c.lineitemId) == lineItemId);
            if (!editCartItem) return;
            const currentLineItem = new LineItem(editCartItem);
            context.commit('SET_LINE_ITEM', currentLineItem);
            await context.dispatch('updateLineItemPriceAndCal');
            context.dispatch('setMenuItemFromLineItem', currentLineItem);

            const menuCategory = context.getters.getLocationMenu?.find((c) => createSlug(c.name) === context.getters.getCurrentMenuItem?.categoryName?.toLowerCase());
            context.commit('SET_MENU_CATEGORY', menuCategory ?? null);

            const existingRequired = currentLineItem.selectedRequiredToppings?.[0];
            if (existingRequired) {
                const matchingSauceCup = menuCategory?.required?.find((r: RequiredTopping) => r.name === existingRequired.name);
                if (matchingSauceCup) context.commit('SET_LAST_SAUCE_CUP', matchingSauceCup);
            }

            const menuItem = context.getters.getCurrentMenuItem;

            if (!menuItem || !currentLineItem.recipes || currentLineItem.recipes.length === 0) return;

            const recipe = menuItem.recipes.find((r) => r.externalId === currentLineItem.recipes[0].id);

            if (!recipe || !recipe.sizes || recipe.sizes.length === 0 || !currentLineItem.recipes[0].size || currentLineItem.recipes[0].size.length === 0) return;

            const size = recipe.sizes.find((s) => s.externalId === currentLineItem.recipes[0].size[0].id);

            if (!size) return;

            context.commit('SET_SELECTED_CRUST', recipe);
            context.commit('SET_SELECTED_SIZE', size);

            await context.dispatch('setAvailableTopping', { menuItem, recipe, size });

            if (currentLineItem.selectedSauces.length === 0) {
                context.dispatch('setCrustDefaultSauce');
            }

            context.dispatch('validateRequiredSelections');
        },
        async setAvailableTopping(context: ActionContext<OrderState, any>, payload: MenuItemSelection): Promise<AvailableTopping | undefined> {
            try {
                const request = new ToppingReq({
                    sizeId: payload.size.sizeId,
                    recipeId: payload.recipe.recipeId,
                    itemId: payload.menuItem?.itemId ?? '',
                    categoryId: payload.menuItem?.categoryId ?? '',
                });
                const availableTopping: AvailableTopping = await fetchToppings(request);

                if (payload.menuItem.isChicken) {
                    if (top?.location.pathname.startsWith('/pizza-pals') || top?.location.pathname.startsWith('/deals')) {
                        const matchingFlavor = availableTopping.flavors.findIndex((f) => f.name === 'Hot Honey');
                        if (matchingFlavor != -1) {
                            availableTopping.flavors.splice(matchingFlavor, 1);
                        }
                    }
                }
                context.commit('SET_AVAILABLE_TOPPING', availableTopping);
                return availableTopping;
            } catch (error) {
                throw new Error('Failed to fetch toppings for this item.');
            }
        },
        setCrust(context: ActionContext<OrderState, any>, selectedCrust: Recipe): void {
            const crust = new Recipe(selectedCrust);
            context.commit('SET_SELECTED_CRUST', crust);

            const size = crust?.sizes?.find((s) => s.preselected) ?? crust?.sizeDescending[0];
            context.commit('SET_SELECTED_SIZE', size);
            context.dispatch('setCrustRecipeWithSize', size);
            context.dispatch('setCrustDefaultSauce');

            if (crust.isHandTossed) {
                const sauceCup = context.getters.getLastSauceCup ?? context.getters.getSauceCups?.[0];
                if (sauceCup) context.dispatch('updateSauceCup', sauceCup);
            } else {
                context.commit('SET_SAUCE_CUP', null);
            }
        },
        setSize(context: ActionContext<OrderState, any>, size: Size): void {
            context.commit('SET_SELECTED_SIZE', size);
            context.dispatch('setCrustRecipeWithSize', size);
        },
        setCrustRecipeWithSize(context: ActionContext<OrderState, any>, size: Size) {
            const crust = context.state.currentItemDetails.selectedCrust;
            if (!crust) return;

            const crustRecipesWithSize: Array<CartRecipe> = size ? crust?.recipeLineItem([size]) : [];
            context.commit('SET_RECIPES', crustRecipesWithSize);

            context.dispatch('updateLineItemPriceAndCal');
        },
        setCrustDefaultSauce(context: ActionContext<OrderState, any>): void {
            if (context.state.currentItemDetails.isSauceCustomized) return;
            const crust = context.state.currentItemDetails.selectedCrust;
            const availableTopping = context.state.currentItemDetails.availableTopping;
            if (!availableTopping || availableTopping.sauces.length === 0) {
                return;
            }
            const matchingDefaultSauce = availableTopping?.sauces.find((sauce) => sauce.name.toUpperCase() === crust?.defaultSauce?.sauce?.toUpperCase());

            if (matchingDefaultSauce) {
                context.commit('SET_CRUST_DEFAULT_SAUCE', matchingDefaultSauce);
            }
        },
        updateSauceTopping(context: ActionContext<OrderState, any>, selectedSauce: Topping): void {
            context.commit('SET_IS_SAUCE_CUSTOMIZED', true);
            const defaultSauceName = context.state.currentItemDetails.defaultCrustSauce?.name;

            if (selectedSauce.name.toUpperCase() === defaultSauceName?.toUpperCase()) {
                context.commit('SET_SAUCE_TOPPING', undefined);
            } else {
                const newSauce = new Topping(selectedSauce);
                newSauce.selected = true;
                context.commit('SET_SAUCE_TOPPING', [newSauce]);
            }

            context.dispatch('updateLineItemPriceAndCal');
            context.dispatch('validateRequiredSelections');
        },
        updateMeatOrVeggieTopping(context: ActionContext<OrderState, any>, payload: ReadonlyArray<PizzaToppingOption>): void {
            const lineItem = context.state.currentItemDetails.lineItem;
            if (!lineItem) return;

            context.commit('SET_MEAT_AND_VEGGIE_TOPPING', mergeToppingOptions(lineItem.selectedToppings, [...payload], context.state.currentItemDetails.availableTopping?.toppings ?? []));
            context.dispatch('updateLineItemPriceAndCal');
            context.dispatch('validateRequiredSelections');
        },
        updateSauceCup(context: ActionContext<OrderState, any>, sauceCup: RequiredTopping): void {
            const lineItem = context.state.currentItemDetails.lineItem;
            if (!lineItem) return;
            const topping = new Topping({ ...sauceCup, name: sauceCup.requiredName, selected: true, multiplier: 1 });

            if (lineItem.isTwoSidedCustom) {
                context.commit('SET_SAUCE_CUP_TWO_SIDED', topping);
            } else {
                context.commit('SET_SAUCE_CUP', topping);
            }
            context.commit('SET_LAST_SAUCE_CUP', sauceCup);
        },
        updateFlavorTopping(context: ActionContext<OrderState, any>, flavor: Topping | null): void {
            const newFlavor = flavor ? Object.assign(new Topping(flavor), { selected: true }) : null;
            context.commit('SET_SELECTED_FLAVOR', newFlavor);
            context.dispatch('updateLineItemPriceAndCal');
            context.dispatch('validateRequiredSelections');
        },
        updateDressingTopping(context: ActionContext<OrderState, any>, payload: ReadonlyArray<ToppingQuantityOption>) {
            const dressings =
                context.state.currentItemDetails.availableTopping?.dressings?.map((dressing) => {
                    const match = payload.find((p) => p.value === dressing.name);
                    const topping = new Topping(dressing);
                    topping.selected = !!match;
                    topping.multiplier = match?.quantity ?? 0;
                    return topping;
                }) ?? [];
            context.commit('SET_DRESSING_TOPPING', dressings);
            context.dispatch('updateLineItemPriceAndCal');
            context.dispatch('validateRequiredSelections');
        },
        autoSelectDressingVariety(context: ActionContext<OrderState, any>): void {
            const availableDressings = context.state.currentItemDetails.availableTopping?.dressings;
            if (!availableDressings || availableDressings.length === 0) return;

            //use default to have this be the majority of dressings auto selected
            const defaultDressing = availableDressings.find((d) => d.selected);
            const dressingsToSelect = availableDressings
                .filter((d) => d.name.toUpperCase() !== 'NO DRESSING')
                .map((d) => {
                    return new Topping(d);
                });

            dressingVariety(dressingsToSelect, defaultDressing);

            context.commit('SET_DRESSING_TOPPING', dressingsToSelect);
            context.dispatch('updateLineItemPriceAndCal');
            context.dispatch('validateRequiredSelections');
        },
        updateInstructions(context: ActionContext<OrderState, any>, payload: Array<InstructionOption>): void {
            const instructions = context.state.currentItemDetails.menuCategory?.instructions
                ?.filter((i) => payload.some((p) => p.name === i.name && p.selected))
                ?.map((i) => Object.assign(new Instruction(i), { selected: true }));
            context.commit('SET_INSTRUCTIONS', instructions);
        },
        async addExtraCheeseTopping(context: ActionContext<OrderState, any>): Promise<void> {
            const cheeseTopping = context.state.currentItemDetails.availableTopping?.toppings.find((t) => t.isExtraCheese);
            if (!cheeseTopping) return;

            const payload: PizzaToppingOption = {
                name: cheeseTopping.name,
                selected: true,
                multiplier: 1,
                placement: PizzaSideEnum.all,
            };
            context.dispatch('updateMeatOrVeggieTopping', [payload]);
        },
        updateLineItemQuantity(context: ActionContext<OrderState, any>, quantity: number): void {
            context.commit('SET_LINE_ITEM_QUANTITY', quantity);
        },
        resetCurrentItems(context: ActionContext<OrderState, any>): void {
            context.commit('RESET_CURRENT_ITEM_DETAILS');
        },
        async updateLineItemPriceAndCal(context: ActionContext<OrderState, any>): Promise<void> {
            const lineItem = context.state.currentItemDetails.lineItem;
            if (!lineItem || !lineItem.id) return;
            try {
                const priceAndCalResponse = await itemPrice(lineItem);
                if (!priceAndCalResponse) {
                    return;
                }
                context.commit('SET_PRICE_AND_CAL', priceAndCalResponse);
                context.commit('SET_LINE_ITEM_PRICE', +priceAndCalResponse.itemPrice);
                return;
            } catch (error) {
                console.log('Error fetching item price: ', error);
            }
        },
        validateRequiredSelections(context: ActionContext<OrderState, any>) {
            const newLineItem = context.state.currentItemDetails.lineItem;
            const currentMenuItem = context.state.currentItemDetails.menuItem;
            const availableTopping = context.state.currentItemDetails.availableTopping;
            if (!newLineItem) return;

            const overLimit = isToppingLimitReached(newLineItem.selectedToppings, currentMenuItem);
            const overLimitMessage = getOverLimitMessage(currentMenuItem);

            if (currentMenuItem?.itemDisplayName === CustomMenuItemNameEnum.HalfAndHalf) {
                const eitherSideOverLimit = overLimit || isToppingLimitReached(newLineItem.selectedToppings2, currentMenuItem);
                const hasHalfToppings = isHalfSigToppingsValid(newLineItem);
                context.commit('SET_IS_TOPPINGS_VALID', hasHalfToppings && !eitherSideOverLimit);
                context.commit('SET_REQUIRED_SELECTION_MESSAGE', !hasHalfToppings ? 'Please add toppings to both halves of your pizza.' : eitherSideOverLimit ? overLimitMessage : null);
                return;
            }

            if (currentMenuItem?.isPizza || currentMenuItem?.isBakery || currentMenuItem?.isCalzato) {
                const hasCoverage = isPizzaCoverageValid(newLineItem.selectedToppings);
                context.commit('SET_IS_TOPPINGS_VALID', hasCoverage && !overLimit);
                context.commit('SET_REQUIRED_SELECTION_MESSAGE', !hasCoverage ? 'Please add toppings to cover your item.' : overLimit ? overLimitMessage : null);
            } else if (currentMenuItem?.isChicken || (currentMenuItem?.isAppetizer && availableTopping?.flavors && availableTopping.flavors.length > 0)) {
                const hasFlavors = newLineItem.selectedFlavors.filter((f) => f.selected).length > 0;
                context.commit('SET_IS_TOPPINGS_VALID', hasFlavors);
                context.commit('SET_REQUIRED_SELECTION_MESSAGE', hasFlavors ? null : 'Please select at least one flavor.');
            } else if (currentMenuItem?.isSalad) {
                const hasDressing = newLineItem.selectedDressings.find((dressing) => dressing.multiplier > 0);
                context.commit('SET_IS_TOPPINGS_VALID', !!hasDressing);
                context.commit('SET_REQUIRED_SELECTION_MESSAGE', hasDressing ? null : 'Please choose a dressing.');
            } else {
                context.commit('SET_IS_TOPPINGS_VALID', !overLimit);
                context.commit('SET_REQUIRED_SELECTION_MESSAGE', overLimit ? overLimitMessage : null);
            }
        },
        updateHalfToppingSelection(context: ActionContext<OrderState, any>, payload: TwoSidedSelection): void {
            const menuItem = context.state.currentItemDetails.menuItem;
            const lineItem = context.state.currentItemDetails.lineItem;
            if (!menuItem || !lineItem) return;

            const toppingsPool = context.state.currentItemDetails.availableTopping?.toppings ?? [];
            const updatedLineItem = mapHalfAndHalfToLineItem(lineItem, toppingsPool, payload);

            context.commit('SET_LINE_ITEM', updatedLineItem);
            context.dispatch('validateRequiredSelections');
            if (context.state.currentItemDetails.isToppingsValid) {
                context.dispatch('updateLineItemPriceAndCal');
            }
        },
        setIsToppingModalOpen(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_IS_TOPPING_MODAL_OPEN', val);
        },
        setSkipPriceAlignment(context: ActionContext<AccountState, any>, val: boolean) {
            context.commit('SET_SKIP_PRICE_ALIGNMENT', val);
        },
        setRecommendItems(context: ActionContext<OrderState, any>): void {
            const recommendItems = context.getters.getLocationMenu.map(
                (cat: MenuCategory) =>
                    randomlyFillArr(
                        cat.items.filter((i) => !i.isPartySize && i.isAddOn),
                        1
                    )[0]
            );
            context.commit('SET_RECOMMEND_ITEMS', recommendItems);
        },
        async initCart(context: ActionContext<OrderState, any>, lineItems: LineItem[]): Promise<void> {
            const accountId = context.getters.getAccountId;

            if (lineItems.length > 0 && context.getters.getCartItems?.length == 0) {
                for (const item of lineItems) {
                    item.customerId = accountId;
                    context.commit('ADD_CART_ITEM', new LineItem(item));
                }
                await context.dispatch('alignPrice');
            }
            // use local to replace API cart
            else if (accountId) {
                const fullOrder = await replaceCart(context.getters.getFullOrder);
                context.commit('CLEAR_CART');
                context.commit('UPDATE_PRICES', fullOrder);
                context.commit('UPDATE_ESTIMATED_TIME', fullOrder.orderHeader?.submitResult.promiseTime.date);
                for (const item of fullOrder.orderDetails?.lineItems ?? []) {
                    context.commit('ADD_CART_ITEM', item);
                }
            }
        },
        resetCart(context: ActionContext<OrderState, any>, lineItems: LineItem[]): void {
            const accountId = context.getters.getAccountId;
            context.commit('CLEAR_CART');

            lineItems.forEach((item) => {
                item.customerId = accountId;
                context.commit('ADD_CART_ITEM', new LineItem(item));
            });
            replaceLocalStorage(localStorageEnum.cart, context.getters.getCartItems, 1);
            context.dispatch('alignPrice');
        },
        addDonation(context: ActionContext<OrderState, any>, item: LineItem): void {
            const cartDonation = context.getters.getCartDonation;
            if (cartDonation) {
                item.lineitemId = cartDonation.lineitemId;
                context.dispatch('updateCartItem', item);
            } else {
                context.dispatch('addCartItem', item);
            }
        },
        removeDonation(context: ActionContext<OrderState, any>): void {
            const cartDonation = context.getters.getCartDonation;
            if (cartDonation) {
                context.dispatch('deleteCartItem', cartDonation);
            }
        },
        async addCartItem(context: ActionContext<OrderState & AccountState, any>, item?: LineItem): Promise<LineItem[]> {
            const { trackAddItem } = useGoogleAnalytics();
            const itemToAdd = item ?? context.state.currentItemDetails.lineItem;
            if (!itemToAdd) return [];
            trackAddItem(itemToAdd?.name);
            try {
                context.commit('SET_CART_IS_UPDATING', true);
                const accountId = context.getters.getAccountId;
                if (accountId) {
                    context.dispatch('validateBasketItems');
                    itemToAdd.customerId = accountId;
                    const res = await addToCart(itemToAdd);
                    itemToAdd.lineitemId = res.lineItems[res.lineItems.length - 1].lineitemId;
                    context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                } else {
                    itemToAdd.lineitemId = context.getters.getCartItems.length > 0 ? Math.max(...context.getters.getCartItems.map((item) => item.lineitemId ?? 0)) + 1 : 0;
                }
                context.commit('ADD_CART_ITEM', itemToAdd);
                await context.dispatch('alignPrice');

                return context.getters.getCartItems;
            } catch (error) {
                throw errorToast(error as string);
            } finally {
                context.commit('SET_CART_IS_UPDATING', false);
            }
        },
        addUpsellToCart(context: ActionContext<OrderState, any>, upsell: MenuItemSelection): Promise<LineItem[]> | undefined {
            if (!upsell) return;

            // built here and passed in explicitly so currentItemDetails keeps holding the item the user is adding.
            // an upsell add-on carries no toppings of its own, so there is nothing to fetch for it
            const lineItem = mapMenuItemToLineItem(upsell.menuItem, upsell.size, upsell.recipe, new AvailableTopping({} as AvailableTopping));

            return context.dispatch('addCartItem', lineItem);
        },
        async initQuickAddLineItem(context: ActionContext<OrderState, any>, payload: MenuItemSelection): Promise<void> {
            context.commit('SET_IS_NEW_LINE_ITEM', true);
            context.commit('SET_MENU_ITEM', payload.menuItem);
            context.commit('SET_SELECTED_CRUST', payload.recipe);
            context.commit('SET_SELECTED_SIZE', payload.size);
            const availableTopping = await context.dispatch('setAvailableTopping', payload);
            const lineItem = mapMenuItemToLineItem(payload.menuItem, payload.size, payload.recipe, availableTopping);
            context.commit('SET_LINE_ITEM', lineItem);
            context.dispatch('setLineItemDefaults');
        },
        addQuickMenuItemToCart(context: ActionContext<OrderState, any>, item: MenuItem): void {
            if (!context.state.currentItemDetails.availableTopping) return;
            const itemToAdd = simpleLineItemMapper(item, 1, context.state.currentItemDetails.availableTopping);
            const size = item.firstRecipe?.sizes?.find((s) => s.price === item.itemPrice);
            if (size) {
                itemToAdd.recipes = item.firstRecipe?.recipeLineItem([size]);
            }
            context.dispatch('addCartItem', itemToAdd);
        },
        addOrderToCart(context: ActionContext<OrderState, any>, order: HistoryOrder): Promise<void> {
            const { trackAddItem } = useGoogleAnalytics();

            return new Promise((resolve, reject) => {
                const accountId = context.getters.getAccountId;
                if (accountId) {
                    addOrderToCart(order.orderHeaderId, accountId)
                        .then(
                            (res: AddToCartRes) => {
                                context.commit('CLEAR_CART'); // clear cart out, then replace with api response
                                //
                                // TODO: Force users to select a location if one is not already and they are attempting to re-order
                                res.lineItems.forEach((item) => {
                                    context.commit('ADD_CART_ITEM', new LineItem(item));
                                    trackAddItem(item.name);
                                });

                                context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                                context.dispatch('alignPrice');
                                resolve(context.getters.getCartItems);
                            },
                            (error) => reject(errorToast(error))
                        )
                        .catch((error) => reject(errorToast(error)));
                }
            });
        },
        async updateCartItem(context: ActionContext<OrderState, any>, lineItem?: LineItem): Promise<LineItem[]> {
            const item = lineItem ?? context.state.currentItemDetails.lineItem;
            if (!item) return [];
            const accountId = context.getters.getAccountId;
            context.commit('UPDATE_CART_ITEM', item);
            if (accountId) {
                context.commit('SET_CART_IS_UPDATING', true);
                context.dispatch('validateBasketItems');
                item.customerId = accountId;
                updateCartItem(item)
                    .then(async (res) => {
                        context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                        await context.dispatch('alignPrice');
                    })
                    .catch((error) => {
                        console.log('Error: ' + error);
                    })
                    .finally(() => {
                        context.commit('SET_CART_IS_UPDATING', false);
                    });
            } else {
                await context.dispatch('alignPrice');
            }
            return context.getters.getCartItems;
        },

        deleteCartItem(context: ActionContext<OrderState & AccountState, any>, item: LineItem): Promise<void> {
            const { trackRemoveItem } = useGoogleAnalytics();
            return new Promise((resolve, reject) => {
                const accountId = context.getters.getAccountId;
                const deletePayload = { customerId: accountId, lineitemId: item.lineitemId };
                trackRemoveItem(item.name);
                context.commit('REMOVE_CART_ITEM', item);
                context.dispatch('removeDonationIfOnlyItem');
                context.dispatch('validateBasketItems');
                // alignPrice skips an empty cart, so report the emptied cart here
                if (context.getters.getCartQuantity == 0) {
                    trackCartUpdated([], { subtotal: 0, tax: 0, shipping: 0, total: 0 });
                }
                if (accountId) {
                    context.commit('SET_CART_IS_UPDATING', true);
                    deleteFromCart(deletePayload)
                        .then(async (res) => {
                            context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                            context.dispatch('alignPrice');
                            resolve();
                        })
                        .catch((error) => reject(errorToast(error)))
                        .finally(() => context.commit('SET_CART_IS_UPDATING', false));
                } else {
                    context.dispatch('alignPrice');
                    resolve();
                }
            });
        },
        removeDonationIfOnlyItem(context: ActionContext<OrderState, any>) {
            const cartItems = context.getters.getCartItems;
            if (cartItems.length == 1 && cartItems[0].isDonation) {
                context.dispatch('removeDonation');
            }
        },
        updateTip(context: ActionContext<OrderState, any>, tip: number): void {
            context.commit('UPDATE_TIP', tip);
            replaceLocalStorage(localStorageEnum.grandTotal, context.getters.getGrandTotal, 0.04); // Dynatrace Data
        },
        alignPrice(context: ActionContext<OrderState, any>): Promise<any> | undefined {
            if (context.getters.getSkipPriceAlignment) {
                return;
            }
            if (context.getters.getCartQuantity == 0) {
                return;
            }

            return orderPrice(context.getters.getFullOrder)
                .then((res) => {
                    if (res && !Array.isArray(res) && typeof res === 'object') {
                        const order = new FullOrder(res);
                        context.commit('UPDATE_PRICES', order);
                        context.commit('UPDATE_ESTIMATED_TIME', order.orderHeader?.submitResult.promiseTime.date);
                        context.commit('UPDATE_ITEM_PRICES', order);
                        context.dispatch('updateLatestMessage', order);
                        context.dispatch('realignCoupons', order);
                        replaceLocalStorage(localStorageEnum.grandTotal, context.getters.getGrandTotal, 0.04); // Dynatrace Data
                        // every cart change reprices through here, so this is where Braze sees the cart
                        trackCartUpdated(context.getters.getCartItems, cartTotalsFromGetters(context.getters));
                    } else {
                        if (res != null) {
                            throw 'Error: ' + res;
                        }
                    }
                })
                .catch((error) => {
                    console.log('Error: ' + error);
                })
                .finally(() => {
                    replaceLocalStorage(localStorageEnum.itemsInCart, context.getters.getCartQuantity, 0.04);
                });
        },
        addOrderPayment(context: ActionContext<OrderState, any>, payment: any): void {
            const allPayments = context.state.payments;
            const isMatchingPayment = allPayments.some((p) => payment.paymentType === PaymentIdEnum.giftCard && p.cardNumber === payment.cardNumber);
            if (isMatchingPayment) return;

            context.commit('ADD_ORDER_PAYMENT', payment);
        },
        updateOrderPayments(context: ActionContext<OrderState, any>): void {
            const grandTotal = context.getters.getGrandTotal;
            context.commit('UPDATE_ORDER_PAYMENTS', grandTotal);
        },
        clearOrderPayments(context: ActionContext<OrderState, any>) {
            context.commit('CLEAR_ORDER_PAYMENTS');
        },
        removeOrderPayment(context: ActionContext<OrderState, any>, payment: Payment): void {
            context.commit('REMOVE_ORDER_PAYMENT', payment);
        },
        updateEstimatedTime(context: ActionContext<OrderState, any>, date: string): void {
            context.commit('UPDATE_ESTIMATED_TIME', date);
        },
        updateLatestMessage(context: ActionContext<OrderState, any>, order: FullOrder): void {
            if (!['/cart', '/checkout'].includes(window.location.pathname)) return;
            //@ts-ignore
            const newMessage = order.orderHeader?.submitResult?.messages[0];
            if (newMessage && newMessage !== context.state.latestMessage) errorToast(newMessage);
            context.commit('UPDATE_LATEST_MESSAGE', newMessage);
        },
        realignCoupons(context: ActionContext<OrderState, any>, order: FullOrder): void {
            context.commit('REPLACE_ALL_COUPONS', order.orderDetails.coupons);
        },
        setOrderProcessing(context: ActionContext<OrderState, any>, status: boolean): void {
            context.commit('SET_ORDER_PROCESSING', status);
        },
        submitOrder(context: ActionContext<OrderState & AccountState, any>, { order, cvv }: { order: FullOrder; cvv?: string }): Promise<any> {
            context.commit('SET_ORDER_PROCESSING', true);
            context.commit('SET_ORDER_ERROR');
            context.commit('SET_PAYMENT_AMOUNTS', order);
            const { trackOrderPlacement, trackOrderPlacementError, trackProductPurchase, trackCouponPurchase } = useGoogleAnalytics();
            const logistics: OrderLogistics = order.orderDetails.orderLogistics;
            if (stringToBool(logistics.isImmediate)) {
                logistics.time = DateTime.now().toLocaleString(DateTime.TIME_24_SIMPLE);
                order.orderDetails.orderLogistics.time = logistics.time;
                context.dispatch('initStoreLocation', logistics ? new OrderLogistics(logistics) : undefined);
            }

            order.orderDetails.loyaltyDiscountAmount = context.state.appliedRewardDiscount;

            return new Promise((resolve, reject) => {
                submitOrder(order, cvv, getBrazeCartId())
                    .then((res) => {
                        // the API sent order_placed with this cart_id; the next cart is a new one
                        resetBrazeCartId();
                        // fetchOrderDetail(res.orderHeaderID)
                        //   .then((responseOrder) => {
                        // todo fetchOrderDetail should be returning all of this.. but for now, rebuild from memory
                        const confirmation = buildOrderConfirmation(order, res);
                        const appliedRewards = getLocalStorage(localStorageEnum.appliedRewards);
                        if (appliedRewards) {
                            confirmation.orderDetails.submittedRewardItems = appliedRewards;
                        }
                        context.commit('SUBMITTED_ORDER', confirmation);
                        context.commit('CLEAR_CART');
                        context.dispatch('resetBundle');
                        const storedOrders = getLocalStorage(localStorageEnum.confirmation) ?? [];
                        setLocalStorage(localStorageEnum.confirmation, [...storedOrders, confirmation], 1);
                        resolve(confirmation);

                        confirmation.orderDetails.lineItems.forEach((item) => trackProductPurchase(item.id, item.displayName, item.price, item.quantity));
                        confirmation.orderDetails.coupons.forEach((coupon) => trackCouponPurchase(coupon.code, coupon.name));
                        trackOrderPlacement(confirmation.orderHeaderID, confirmation.orderDetails.taxTotal, confirmation.orderDetails.grandTotal);

                        if (context.getters.isLoggedIn) {
                            successToastDetail({
                                message: `<h1 class='mt-0'>We're on it!</h1><img src="${require('@/assets/pep/successPep.png')}"/><br/>Points arrive after you get your food.<br/> <small>May take up to 24 hours.</small>`,
                                position: 'middle',
                                color: 'light',
                                duration: 3000,
                                cssClass: 'rewards-toast',
                            });
                        }
                        if (context.getters.getAccount?.customerId) context.commit('SET_ORDER_HISTORY');
                    })
                    // })
                    .catch((err) => {
                        trackOrderPlacementError(err);
                        reject(context.commit('SET_ORDER_ERROR', err));
                    })
                    .finally(() => context.commit('SET_ORDER_PROCESSING', false));
            });
        },
        // todo use fetchOrderDetail
        fetchSubmittedOrder(context: ActionContext<OrderState, any>, id: string) {
            const foundOrder = getLocalStorage(localStorageEnum.confirmation)?.find((o) => o.orderHeaderID == id);
            context.commit('SUBMITTED_ORDER', foundOrder);
        },
        addCoupon(context: ActionContext<OrderState, any>, coupon: Coupon) {
            context.commit('ADD_COUPON', coupon);
            const isLoggedIn = context.getters.isLoggedIn;
            if (isLoggedIn) {
                context.dispatch('validateBasketItems');
            }
            return context.dispatch('alignPrice');
        },
        removeCoupon(context: ActionContext<OrderState, any>, coupon: Coupon) {
            context.commit('REMOVE_COUPON', coupon);
            const isLoggedIn = context.getters.isLoggedIn;
            if (isLoggedIn) {
                context.dispatch('validateBasketItems');
            }
            return context.dispatch('alignPrice');
        },
        resetCoupons(context: ActionContext<OrderState, any>, alignPrice = false) {
            context.commit('RESET_COUPONS');
            if (alignPrice) return context.dispatch('alignPrice');
            return null;
        },
        removeOrderError(context: ActionContext<OrderState, any>) {
            context.commit('SET_ORDER_ERROR', undefined);
        },
        clearCart(context: ActionContext<OrderState, any>) {
            context.commit('CLEAR_CART');
        },
        logoutCart(context: ActionContext<OrderState, any>) {
            context.commit('LOGOUT_CART');
            resetBrazeCartId();
        },
        cartEditLoader(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_CARTEDIT_LOADER', val);
        },
        setRewardsValidating(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_REWARDS_VALIDATING', val);
        },
        async validateBasketItems(context: ActionContext<OrderState & AccountState, any>): Promise<void> {
            const cartItems = context.getters.getCartItems;
            const rewardsBasketInfo = context.getters.getRewardsBasketInfo;
            const subTotal = context.getters.getSubTotalNoDonation ?? 0;
            const subTotalMinusDiscounts = subTotal - context.getters.getCouponsDiscount;

            if (!rewardsBasketInfo || rewardsBasketInfo.basketItems.length === 0 || cartItems.length === 0 || !subTotalMinusDiscounts) {
                context.commit('SET_REWARDS_VALIDATING', false);
                return;
            }

            const itemId = cartItems[0].id;
            const storeNumber = itemId.split(',')[0].split('=')[1];

            context.commit('SET_REWARDS_NEED_VALIDATION', true);
            try {
                const validateBasketPayload: ValidateBasketPayload = {
                    storeNumber: storeNumber,
                    lineItems: cartItems,
                    receiptDatetime: new Date().toISOString(),
                    subtotalAmount: subTotalMinusDiscounts,
                    receiptAmount: subTotalMinusDiscounts,
                };
                const response: BasketValidationDTO = await validateRewardBasketItems(validateBasketPayload);
                if (response) {
                    const discounts = response.success.map((success) => success.discountAmount);
                    const totalRewardDiscount = discounts.reduce((acc: number, curr: any) => acc + curr, 0);
                    context.commit('SET_REWARD_DISCOUNT', totalRewardDiscount);
                    context.commit('SET_BASKET_ITEM_FAILURES', response.failures);
                    context.commit('SET_VALIDATED_BASKET_ITEMS', response.success);
                }
            } catch (error) {
                console.log('Error validating rewards: ' + error);
                errorToast('Unable to process rewards at this time.');
            } finally {
                context.commit('SET_REWARDS_VALIDATING', false);
                context.commit('SET_REWARDS_NEED_VALIDATION', false);
            }
        },
    },
    getters: {
        getAvailableTopping: (state: OrderState): AvailableTopping | null => state.currentItemDetails.availableTopping,
        getPriceAndCal: (state: OrderState): ItemPriceCal | null => state.currentItemDetails.priceAndCal,
        getInstructions: (state: OrderState): ReadonlyArray<Instruction> | null => state.currentItemDetails.menuCategory?.instructions ?? null,
        getSelectedCrust: (state: OrderState): Recipe | null => state.currentItemDetails.selectedCrust,
        getSelectedSize: (state: OrderState): Size | null => state.currentItemDetails.selectedSize,
        getDefaultCrustSauce: (state: OrderState): Topping | null => state.currentItemDetails.defaultCrustSauce,
        getMenuCategory: (state: OrderState): MenuCategory | null => state.currentItemDetails.menuCategory,
        getSauceCups: (state: OrderState): ReadonlyArray<RequiredTopping> | null => state.currentItemDetails.menuCategory?.required ?? null,
        getLastSauceCup: (state: OrderState): RequiredTopping | null => state.currentItemDetails.lastSauceCup,
        getCurrentMenuItem: (state: OrderState): MenuItem | null => state.currentItemDetails.menuItem,
        getCurrentLineItem: (state: OrderState): LineItem | null => state.currentItemDetails.lineItem,
        getIsNewLineItem: (state: OrderState): boolean => state.currentItemDetails.isNewLineItem,
        getIsToppingsValid: (state: OrderState): boolean => state.currentItemDetails.isToppingsValid,
        getToppingValidationMessage: (state: OrderState): string | null => state.currentItemDetails.requiredSelectionMessage,
        getRecommendItems: (state: OrderState): ReadonlyArray<MenuItem> | null => state.recommendItems,
        // scoped to the item because a size externalId is a slot number that repeats across items.
        // undefined when a store doesn't carry the item or that size of it
        getMenuItemSelection:
            (state: OrderState, getters: any) =>
            ({ itemName, sizeExternalId }: MenuItemSelectionLookup): MenuItemSelection | undefined => {
                const matchesSize = (s: Size) => s.externalId === sizeExternalId;
                const menuItem: MenuItem | undefined = getters.getLocationMenu?.flatMap((c: MenuCategory) => c.items).find((i: MenuItem) => i.itemName.toUpperCase() === itemName.toUpperCase());
                const recipe = menuItem?.recipes?.find((r: Recipe) => r.sizes?.some(matchesSize));
                const size = recipe?.sizes?.find(matchesSize);

                return menuItem && recipe && size ? { menuItem, recipe, size } : undefined;
            },
        // Housemade Ranch is one size on the shared sauce cup item
        getRanchUpsell: (state: OrderState, getters: any): MenuItemSelection | undefined => getters.getMenuItemSelection({ itemName: SAUCE_CUP_ITEM_NAME, sizeExternalId: HOUSEMADE_RANCH_SIZE_ID }),
        getIsToppingModalOpen: (state: OrderState): boolean => state.isToppingModalOpen,
        getCouponsDiscount: (state: OrderState): number => {
            // @ts-ignore
            const discountArray = state.validCoupons.map((coupon) => parseFloat(coupon.discount));
            if (discountArray.length > 0) {
                const total = discountArray.reduce((partialSum, a) => (partialSum ?? 0) + (a ?? 0), 0) ?? 0.0;
                return total;
            }
            return 0.0;
        },
        getDeliveryCharge: (state: OrderState): number => +(state.deliveryCharge ?? 0).toFixed(2),
        getCartItems: (state: OrderState): ReadonlyArray<LineItem> => state.cartItems,
        getCartItemsSorted: (state: OrderState): ReadonlyArray<LineItem> =>
            [...state.cartItems]
                .filter((i) => {
                    if (!i.bundleIdentifier) {
                        return true;
                    } else {
                        return i.bundleIdentifier.startsWith('vm');
                    }
                })
                .reverse()
                .sort((a, b) => a.lineitemId ?? 0 - (b?.lineitemId ?? 0)),
        getCartBundleItemsSorted: (state: OrderState): ReadonlyArray<LineItem> => {
            const items = [...state.cartItems].filter((i) => i.bundleIdentifier && !i.bundleIdentifier.startsWith('vm'));
            const groupBy = function (xs, key) {
                return xs.reduce(function (rv, x) {
                    const val = x[key].split('--')[0];
                    (rv[val] = rv[val] || []).push(x);
                    return rv;
                }, {});
            };

            return groupBy(items, 'bundleIdentifier');
        },
        getCartItemsNoDonation: (state: OrderState): ReadonlyArray<LineItem> => [...state.cartItems].filter((item) => !item.isDonation).sort((a, b) => a.lineitemId ?? 0 - (b?.lineitemId ?? 0)),
        getCartDonation: (state: OrderState, getters: any): LineItem => getters.getCartItems.find((item) => item.isDonation),
        getIsCartUpdating: (state: OrderState): boolean => state.isCartUpdating,
        getCartItemsNameSort: (state: OrderState): ReadonlyArray<LineItem> => [...state.cartItems].sort((a, b) => a.name.localeCompare(b.name)),
        getCartQuantity: (state: OrderState): number => state.cartItems.reduce((t, item) => t + +item.quantity, 0),
        getSubTotal: (state: OrderState, getters: any): number => +getters.getCartItems.reduce((prev, curr) => prev + curr.quantity * curr.price, 0).toFixed(2),
        getSubTotalPlusTip: (state: OrderState, getters: any): number =>
            (+getters.getCartItems.reduce((prev, curr) => prev + curr.quantity * curr.price, 0) + getters.getTip + getters.getTax + getters.getDeliveryCharge).toFixed(2),
        getSubTotalNoDonation: (state: OrderState, getters: any): number => +getters.getCartItemsNoDonation.reduce((prev, curr) => prev + curr.quantity * curr.price, 0).toFixed(2),
        getDonationAmount: (state: OrderState): number => state.cartItems.find((c) => c.isDonation)?.price ?? 0,
        getTip: (state: OrderState): number => state.tip,
        getTax: (state: OrderState): number => state.taxTotal,
        getGrandTotal: (state: OrderState, getters: any): number => +getters.getSubTotalPlusTip - getters.getCouponsDiscount - getters.getAppliedRewardDiscount,
        getGrandTotalNoDonation: (state: OrderState, getters: any): number => getters.getSubTotalNoDonation + state.tip + state.taxTotal + getters.getDeliveryCharge - getters.getCouponsDiscount,
        getOrderTotals: (state: OrderState, getters: any): OrderTotals => {
            return new OrderTotals({
                tip: getters.getTip,
                tax: getters.getTax,
                cartTotal: getters.getSubTotal,
                donation: getters.getDonationAmount,
                deliveryCharge: state.deliveryCharge,
                grandTotal: getters.getGrandTotal,
            });
        },
        getEstimatedTime: (state: OrderState): DateTime | undefined => state.estimatedTime,
        getValidCoupons: (state: OrderState): ReadonlyArray<Coupon> => state.validCoupons,
        getPayments: (state: OrderState): ReadonlyArray<Payment> => state.payments,
        getPaymentCoversTotal: (state: OrderState, getters: any): boolean => {
            if (state.payments.length === 0) {
                return getters.getGrandTotal === 0;
            }

            const isCashOrCreditPayment = state.payments.some((p) => p.paymentType != PaymentIdEnum.giftCard);
            if (isCashOrCreditPayment) return true;

            const giftCardTotal = state.payments.filter((p) => p.paymentType === PaymentIdEnum.giftCard)?.reduce((total, payment) => total + +payment.balance, 0);
            if (giftCardTotal >= getters.getGrandTotal) return true;

            return false;
        },
        getOrderProcessing: (state: OrderState): boolean => state.orderProcessing,
        getOrderError: (state: OrderState): string | undefined => state.orderError,
        getSubmittedOrder: (state: OrderState): FullOrder | undefined => state.submittedOrder,
        getPrimaryPayment: (state: OrderState): Payment | undefined => state.payments[0],
        getSecondaryPayment: (state: OrderState): Payment | undefined => state.payments[1],
        getPaymentType: (state: OrderState): PaymentIdEnum | undefined => {
            if (state.payments.length > 1) {
                return PaymentIdEnum.giftCard;
            } else if (state.payments.length == 1) {
                return state.payments[0].paymentType;
            } else {
                return undefined;
            }
        },
        getLatestMessage: (state: OrderState): string => state.latestMessage,
        getCartEditLoader: (state: OrderState): boolean => state.isEditCartLoader,
        getSkipPriceAlignment: (state: OrderState): boolean => state.skipPriceAlignment,
        getAppliedRewardDiscount: (state: OrderState): number => state.appliedRewardDiscount,
        getRewardItemNameFailures: (state: OrderState): ReadonlyArray<string> => state.rewardItemNameFailures,
        getValidatedBasketItems: (state: OrderState): ReadonlyArray<ValidatedBasketItem> => state.validatedBasketItems,
        getIsRewardsValidating: (state: OrderState): boolean => state.isRewardsValidating,
        getRewardsNeedValidation: (state: OrderState): boolean => state.rewardsNeedValidation,
    },
};
